import {singleton} from "tsyringe";
import {ReactiveController, ReactiveControllerHost} from "lit";
import {FormController} from "../components/_reactive-controllers/form.controller";
import {FieldType} from "@ha/formhandler";
import {Issue} from "../domain/entities/issue.entity";
import {IssueStatus} from "../domain/entities/issue-status.entity";
import {IssueType} from "../domain/entities/issue-type.entity";
import {Project} from "../domain/entities/project.entity";
import {Keyword} from "../domain/entities/keyword.entity";
import {Employee} from "../domain/entities/employee.entity";
import {Interval} from "luxon";
import type {RteValue} from "../domain/rte-value.type";
import {RteValueHelper} from "../helpers/rte-value.helper";
import {EmployeeToIssueAssignment} from "../domain/entities/employee-to-issue-assignment.entity";
import {FormFieldConfiguration} from "@ha/formhandler/types/form-field-configuration";
import {DateRangeOrderValidator} from "../validators/date-range-order.validator";
import {get} from "lit-translate";

/**
 * Form data structure for Issue editing dialog
 */
type FormData = {
    title: string,
    status: IssueStatus | null,
    type: IssueType,
    project: Project,
    keywords: Keyword[],
    timeRange: Interval,
    creator: Employee,
    description: RteValue,
    employeeToIssueAssignments: EmployeeToIssueAssignment[]
}

/**
 * Service for managing Issue form controllers and form definitions.
 *
 * This service centralizes form creation logic and eliminates code duplication
 * by providing reusable form definitions for all Issue dialogs.
 *
 * **DRY Principle**: Form definitions exist only once, not per Issue
 * **SRP Principle**: Only responsible for form management
 * **KISS Principle**: Simple API with clear methods
 */
@singleton()
export class IssueFormService {

    /**
     * Map to store form controllers by Issue UID
     * This prevents creating duplicate controllers for the same Issue
     */
    private _formControllers = new Map<string, FormController<FormData>>();

    /**
     * Creates or retrieves a form controller for the given Issue.
     *
     * **Benefits over previous approach:**
     * - Eliminates 75+ lines of duplicate code per Issue
     * - Centralizes form logic in one place
     * - Reusable across different components
     *
     * @param host The reactive controller host (usually the dialog component)
     * @param issue The Issue entity to create a form controller for
     * @returns Form controller instance
     */
    createFormController(host: ReactiveController, issue: Issue): FormController<FormData> {
        // Return an existing controller if already created (performance optimization)
        if (this._formControllers.has(issue.uid)) {
            return this._formControllers.get(issue.uid)!;
        }

        // Create a new form controller with centralized field definitions
        const formController = new FormController<FormData>(
            host as ReactiveControllerHost,
            `issue-${issue.uid}`,
            this.getFieldDefinitions(issue)
        );

        // **REACTIVE BINDING MOVED TO DIALOG**
        // Central subscription in Dialog handles entity.persisted$ → form.commit()
        // This provides better lifecycle management and cleanup

        // Store controller for reuse
        this._formControllers.set(issue.uid, formController);
        return formController;
    }

    /**
     * Retrieves an existing form controller for the given Issue.
     *
     * @param issue The Issue entity
     * @returns Form controller instance or undefined if not found
     */
    getFormController(issue: Issue): FormController<FormData> | undefined {
        return this._formControllers.get(issue.uid);
    }

    /**
     * Retrieves an existing form controller by Issue UID.
     * Safer alternative to getFormController() when you only have the UID.
     *
     * @param uid The UID of the Issue
     * @returns Form controller instance or undefined if not found
     */
    getFormControllerByUid(uid: string): FormController<FormData> | undefined {
        return this._formControllers.get(uid);
    }

    /**
     * Checks if a form controller exists for the given Issue UID.
     *
     * @param uid The UID of the Issue
     * @returns True if form controller exists, false otherwise
     */
    hasFormController(uid: string): boolean {
        return this._formControllers.has(uid);
    }

    /**
     * Returns all form controllers managed by this service.
     * Useful for operations across multiple Issues (like save-all).
     *
     * @returns Map of Issue UID to form controller
     */
    getAllFormControllers(): Map<string, FormController<FormData>> {
        return this._formControllers;
    }

    /**
     * Removes a form controller for a specific Issue.
     * Call this when an Issue is deleted or no longer needed.
     *
     * @param issueUid The UID of the Issue
     */
    clearFormController(issueUid: string): void {
        this._formControllers.delete(issueUid);
    }

    /**
     * Clears all form controllers.
     * Call this when the dialog is closed or disconnected.
     */
    clearAllFormControllers(): void {
        this._formControllers.clear();
    }

    /**
     * **CENTRAL FORM DEFINITION - DRY PRINCIPLE IN ACTION**
     *
     * This method contains the form field definitions that were previously
     * duplicated 75+ lines per Issue. Now they exist only once!
     *
     * **Why this is better:**
     * - Single source of truth for field definitions
     * - Changes need to be made only once
     * - No risk of inconsistencies between Issues
     * - Much easier to maintain and test
     *
     * @param issue The Issue entity to create definitions for
     * @returns Array of field definitions
     */
    private getFieldDefinitions(issue: Issue): FormFieldConfiguration[] {
        return [{
            type: FieldType.Primitive,
            key: 'title',
            initialValue: issue.title,
        }, {
            type: FieldType.Primitive,
            key: 'timeRange',
            initialValue: issue.timeRange,
            compare: this.compareDateRange,
            validations: [
                new DateRangeOrderValidator({validationMessageFn: () => get(`components.ha-date-range.start-after-end-error`)})
            ]
        }, {
            type: FieldType.Primitive,
            key: 'status',
            initialValue: issue.status,
        }, {
            type: FieldType.Primitive,
            key: 'type',
            initialValue: issue.type,
        }, {
            type: FieldType.Primitive,
            key: 'project',
            initialValue: issue.project,
        }, {
            type: FieldType.Primitive,
            key: 'keywords',
            initialValue: issue.keywords,
        }, {
            type: FieldType.Primitive,
            key: 'employeeToIssueAssignments',
            initialValue: issue.employeeToIssueAssignments,
            compare: this.createAssignmentBaselineCompare(issue.employeeToIssueAssignments),
        }, {
            type: FieldType.Primitive,
            key: 'creator',
            initialValue: issue.creator,
        }, {
            type: FieldType.Primitive,
            key: 'description',
            initialValue: issue.description,
            compare: this.compareRte
        }];
    }

    /**
     * Builds the comparison for `employeeToIssueAssignments`. Two values count as equal
     * exactly when BOTH still describe the stored state — the assignment uids and time
     * budgets the dialog was opened with.
     *
     * Why a stored baseline instead of comparing the two values with each other: without a
     * `compare` the field falls back to the library's default, `a === b`, and that can never
     * report a changed time budget. `HaEmployeeIssueAssignment._onTimeBudgetChanged` mutates
     * the assignment in place — deliberately, so the command builder emits one update instead
     * of a delete plus a create with a fresh uid — and the field's `initialValue` is the very
     * same array object. Both sides of the comparison move together, so no comparison BETWEEN
     * them can answer the question. The baseline is the missing fixed point, and comparing
     * each side against it is what makes the answer possible at all.
     *
     * The library calls this in three shapes, and all three are served by the rule above
     * (`@ha/formhandler/dist/index.js:1079-1087`):
     * - `compare(incomingValue, storedValue)` gates the setter. It must report "different"
     *   whenever anything moved, otherwise the incoming value is never stored and no listener
     *   is notified. Any non-baseline side makes it do so.
     * - `compare(initialValue, newValue)` detects a reset, and is only reached once the gate
     *   is open.
     * - `compare(initialValue, currentValue)` is `isDirty`, and both arguments are the same
     *   mutating array — this is the call the baseline exists for.
     *
     * Covers every edit this dialog offers: a changed budget leaves the baseline, a budget
     * changed BACK returns to it (which is what re-locks the save button), a swapped assignee
     * brings a uid the baseline does not know, and adding or removing an assignment changes
     * the length.
     *
     * The baseline is rebuilt on every save because the dialog drops its form controller and
     * this service's cached definitions afterwards, so it always describes the stored state.
     *
     * @param assignments The assignments the dialog was opened with
     * @returns A compare function reporting true only while both values are the stored state
     */
    private createAssignmentBaselineCompare(assignments: EmployeeToIssueAssignment[]): (valueA: EmployeeToIssueAssignment[], valueB: EmployeeToIssueAssignment[]) => boolean {
        const budgets = new Map((assignments ?? []).map(assignment => [assignment.uid, assignment.timeBudget]));

        const isBaseline = (value: EmployeeToIssueAssignment[]): boolean =>
            Array.isArray(value)
            && value.length === budgets.size
            && value.every(assignment => budgets.get(assignment.uid) === assignment.timeBudget);

        return (valueA: EmployeeToIssueAssignment[], valueB: EmployeeToIssueAssignment[]): boolean =>
            isBaseline(valueA) && isBaseline(valueB);
    }

    /**
     * Compares two Interval objects for equality.
     *
     * **Extracted from duplicate code**: This logic was repeated in every
     * form controller creation. Now it exists only once.
     *
     * @param dateRangeA First interval
     * @param dateRangeB Second interval
     * @returns True if intervals are equal
     */
    private compareDateRange = (dateRangeA: Interval, dateRangeB: Interval): boolean => {
        if (!dateRangeA && !dateRangeB) return true;
        if (!dateRangeA || !dateRangeB) return false;

        return dateRangeA.equals(dateRangeB);
    }

    /**
     * Compares two `RteValue`s (`Delta | RichTextDocument`, see its own doc
     * comment) for equality — SHAPE-agnostic: `ha-rte`'s live edited value is
     * always a `RichTextDocument`, while `initialValue` (the entity's own
     * `description`) is still a Delta for any row not yet re-saved since the
     * RTE unification (2026-09). Both sides are normalized through
     * `RteValueHelper.toRichTextDocument` before comparing, so a field the
     * user opened, edited, then edited BACK to its original text still
     * re-locks the save button, instead of staying dirty forever purely
     * because its in-memory shape changed.
     */
    private compareRte = (valueA: RteValue | null, valueB: RteValue | null): boolean => {
        if (!valueA && !valueB) return true;
        if (!valueA || !valueB) return false;

        return JSON.stringify(RteValueHelper.toRichTextDocument(valueA))
            === JSON.stringify(RteValueHelper.toRichTextDocument(valueB));
    }
}